import { Link } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { OverflowCues } from "~/components/ui/overflow-cue";
import { observeActiveChildInStrip, useOverflowCue } from "~/components/ui/use-overflow-cue";

export type WorkspaceTabItem = {
  id: string;
  to: string;
  label: string;
  search?: object;
  hash?: string;
};

export function WorkspaceTabs({
  label,
  items,
  active,
}: {
  label: string;
  items: readonly WorkspaceTabItem[];
  active: string;
}) {
  const navRef = useRef<HTMLElement>(null);
  const { overflows, canPrev, canNext, scrollByPage } = useOverflowCue(navRef);

  // Tabs stay on one line and scroll sideways on narrow screens. Bring the
  // current tab into view without moving the page vertically.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `active` changes which link carries aria-current, so the DOM query must re-run.
  useEffect(() => {
    const node = navRef.current;
    if (!node) return;
    return observeActiveChildInStrip(node, '[aria-current="page"]');
  }, [active, overflows]);

  return (
    <div className="overflow-cue-host workspace-tabs-host">
      <nav
        ref={navRef}
        className="workspace-tabs"
        data-overflows={overflows ? "true" : undefined}
        aria-label={label}
        tabIndex={overflows ? 0 : undefined}
      >
        {items.map((item) => (
          <Link
            key={item.id}
            to={item.to as never}
            search={(item.search ?? {}) as never}
            hash={item.hash}
            className="workspace-tabs__link"
            activeOptions={{ exact: true, includeSearch: true }}
            activeProps={{ "aria-current": item.id === active ? "page" : undefined }}
            inactiveProps={{ "aria-current": item.id === active ? "page" : undefined }}
            aria-current={item.id === active ? "page" : undefined}
          >
            {item.label}
          </Link>
        ))}
      </nav>
      <OverflowCues
        canPrev={canPrev}
        canNext={canNext}
        onPrev={() => scrollByPage(-1)}
        onNext={() => scrollByPage(1)}
      />
    </div>
  );
}
